import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2, UserPlus, User, Phone, ArrowLeft } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { toast } from "sonner";

export default function CustomerRegister() {
    const { user, register, loading } = useCustomerAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }
    if (user) return <Navigate to="/conta" replace />;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password.length < 6) {
            setError("A password deve ter pelo menos 6 caracteres");
            return;
        }
        setSubmitting(true);
        try {
            await register({
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
                phone: form.phone || null,
            });
            toast.success("Conta criada com sucesso!");
            nav("/conta", { replace: true });
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro ao criar conta";
            setError(typeof msg === "string" ? msg : "Erro ao criar conta");
            toast.error(typeof msg === "string" ? msg : "Erro ao criar conta");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-brand-cream relative overflow-hidden">
            <div
                aria-hidden
                className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-brand-orange/15 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-brand-green/15 blur-3xl"
            />

            <form
                onSubmit={submit}
                data-testid="customer-register-form"
                className="relative w-full max-w-md bg-white rounded-2xl shadow-pop p-8 md:p-10"
            >
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-ink-soft hover:text-brand-green mb-6"
                    data-testid="customer-register-back"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao site
                </Link>

                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-brand-green flex items-center justify-center">
                        <UserPlus className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-brand-orange">
                            Sabor Líquido
                        </div>
                        <h1 className="font-display font-medium text-2xl text-brand-ink">
                            Criar conta
                        </h1>
                    </div>
                </div>

                <div className="mt-8 space-y-4">
                    <Field icon={User} label="Nome" value={form.name} onChange={set("name")}
                        testid="customer-register-name" type="text" required minLength={2} placeholder="O teu nome" />
                    <Field icon={Mail} label="Email" value={form.email} onChange={set("email")}
                        testid="customer-register-email" type="email" required placeholder="o.teu@email.com" />
                    <Field icon={Lock} label="Password (min. 6)" value={form.password} onChange={set("password")}
                        testid="customer-register-password" type="password" required minLength={6} placeholder="••••••••" />
                    <Field icon={Phone} label="Telefone (opcional)" value={form.phone} onChange={set("phone")}
                        testid="customer-register-phone" type="tel" placeholder="+351 912 345 678" />
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="customer-register-submit"
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-6 py-4 text-sm font-semibold transition-all shadow-pop disabled:opacity-60"
                >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    {submitting ? "A criar conta..." : "Criar conta"}
                </button>

                {error && (
                    <div
                        role="alert"
                        data-testid="customer-register-error"
                        className="mt-4 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm font-semibold text-red-700 text-center"
                    >
                        {error}
                    </div>
                )}

                <p className="mt-6 text-center text-xs text-brand-ink-soft">
                    Já tens conta?{" "}
                    <Link
                        to="/conta/entrar"
                        className="font-bold text-brand-green hover:underline"
                        data-testid="customer-register-to-login"
                    >
                        Entrar
                    </Link>
                </p>
            </form>
        </div>
    );
}

const Field = ({ icon: Icon, label, testid, ...props }) => (
    <div>
        <label className="text-xs font-bold uppercase tracking-widest text-brand-ink-soft mb-1.5 block">
            {label}
        </label>
        <div className="relative">
            <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
            <input
                data-testid={testid}
                {...props}
                className="w-full rounded-2xl border-2 border-brand-green/15 bg-white pl-11 pr-4 py-3.5 text-sm text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green"
            />
        </div>
    </div>
);
